import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TempleInclinationEnum } from '@models/variant-temple-inclination';
import { TranslatePipe } from '@ngx-translate/core';
import { BrandModeService } from '@services/brandmode.service';
import { ManualConfigService } from '@services/manual-config.service';
import { FeaturedGroupConstant } from '@shared/featured-group-constant';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ModelModel } from '@models/api-models/model';
import { ComponentVariantModel } from '@models/component-variant';
import { ConfigurationModel } from '@models/configuration';
import { ModelSizeModel } from '@models/model-size';
import { EventsService } from '@services/events.service';
import { ConfigurationMode, ImageService, ImageSize } from '@services/image.service';
import { InspirationGroupModel } from '../../../../_models/inspiration-group';
import { ManualConfigCreateModel } from '../../../../_models/manual-config-create';
import { EnduserDisplayService } from '../../../../_services/enduserdisplay.service';
import { CustomizerInspirationStateConstants } from '../../../../shared/customizer-inspiration-state-constants';
import { validateDate } from '../../../../shared/utils/date-helper';

@Component({
	selector: 'add-to-inspiration',
	templateUrl: 'add-to-inspiration.html',
	styleUrls: ['./add-to-inspiration.scss'],
	imports: [CommonModule, TranslatePipe, FormsModule],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true
})
export class AddToInspirationComponent implements OnChanges, OnInit, OnDestroy {
	@Input() configuration: ConfigurationModel | null;
	@Input() modelSize?: ModelSizeModel;
	@Input() model?: ModelModel;
	@Input() variants?: Array<ComponentVariantModel>;
	@Input() customizerInspirationState: CustomizerInspirationStateConstants;
	@Input() templeInclination?: TempleInclinationEnum | null;

	@Output() AddedToInspirationClicked: EventEmitter<void> = new EventEmitter();

	public inspirationgroups: Array<InspirationGroupModel> = [];
	public chosenInspirationGroup: number;
	public chosenReleaseYear: number;
	public chosenReleaseMonth: number;
	public chosenReleaseDay: number;

	public currentIsAddedToInspiration: boolean;

	public dateIsInvalid: boolean;

	public enduserDisplayChangedObserveable: Observable<ConfigurationMode>;
	public enduserDisplayChangedSubscription: Subscription;
	public isDisplay: boolean;

	constructor(
		private eventsService: EventsService,
		private enduserDisplayService: EnduserDisplayService,
		private manualConfigService: ManualConfigService,
		private brandModeService: BrandModeService,
		private imageService: ImageService
	) { }

	ngOnInit(): void {
		this.manualConfigService.getInspirationGroups().then(groups => {
			this.inspirationgroups = groups.filter(x => x.Type !== FeaturedGroupConstant.Favorite);
			this.chosenInspirationGroup = this.inspirationgroups[0].Id;
		});
		this.currentIsAddedToInspiration = false;
		this.dateIsInvalid = false;

		this.isDisplay = this.enduserDisplayService.IsDisplay();

		this.enduserDisplayChangedObserveable = this.enduserDisplayService.enduserDisplayChangedObservable();
		this.enduserDisplayChangedSubscription = this.enduserDisplayChangedObserveable.pipe(debounceTime(100)).subscribe(x => {
			this.isDisplay = this.enduserDisplayService.IsDisplay();
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['variants'] && changes['variants'].previousValue) {
			const variantsChanged = !ComponentVariantModel.IsEqualVariants(changes['variants'].previousValue, changes['variants'].currentValue);
			this.currentIsAddedToInspiration = !variantsChanged;
		}

		if (changes['modelSize'] && changes['modelSize'].previousValue) {
			const modelSizeChanged = changes['modelSize'].previousValue.Code != changes['modelSize'].currentValue.Code;
			this.currentIsAddedToInspiration = !modelSizeChanged;
		}

		if (changes['templeInclination']) {
			const templeInclinationChanged = changes['templeInclination'].previousValue !== changes['templeInclination'].currentValue;
			this.currentIsAddedToInspiration = !templeInclinationChanged;
		}
	}

	ngOnDestroy() {
		this.enduserDisplayChangedSubscription.unsubscribe();
	}

	public async AddToInspirationGroup() {
		this.eventsService.showLoader(true);

		const releasedate = new Date(Date.UTC(0, 0, 0, 0, 0, 0));
		releasedate.setUTCFullYear(this.chosenReleaseYear, this.chosenReleaseMonth - 1, this.chosenReleaseDay);

		if (validateDate(releasedate, this.chosenReleaseYear, this.chosenReleaseMonth - 1, this.chosenReleaseDay)) {
			this.dateIsInvalid = false;

			let imageUrls: Array<string> = [];
			if (this.model?.IsRimless) {
				imageUrls.push(this.imageService.GetFrontImage(this.model, this.variants, ImageSize.Medium));
				imageUrls.push(this.imageService.GetSideImage(this.model, this.variants, ImageSize.Medium));

			} else {
				imageUrls.push(this.imageService.GetPerspectiveImage(this.model, this.variants, ImageSize.Medium));
			}
			const manualConfiguration = ManualConfigCreateModel.CreateFromData(this.chosenInspirationGroup, releasedate, this.model,
				this.configuration, this.modelSize, this.variants, this.templeInclination, 0,
				this.brandModeService.isPrecious,
				imageUrls);

			this.manualConfigService.createInspirationConfiguration(manualConfiguration).then(t => {
				this.currentIsAddedToInspiration = true;
				this.AddedToInspirationClicked.emit();
				this.eventsService.showLoader(false);
			});

		} else {
			this.dateIsInvalid = true;
			this.eventsService.showLoader(false);
		}
	}

	public InspirationGroupChanged() {
		this.currentIsAddedToInspiration = false;
	}
}
